import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import { Document, Page, pdfjs } from "react-pdf";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import {
  HiOutlineCheckCircle,
  HiOutlineDownload,
  HiOutlineDocumentText,
  HiOutlineMail,
  HiOutlinePencilAlt,
  HiOutlineUser,
} from "react-icons/hi";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { apiURL, COMPANY_PREFIX } from "../config/config";
import QuotationPreview from "../components/QuotationPreview";
import SignatureModal from "../components/customer-sign/SignatureModal";
import SignatureStatusPanel from "../components/customer-sign/SignatureStatusPanel";
import bankAccounts from "../data/bankAccounts.json";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getCustomerName = (quotation = {}) =>
  quotation.clientId?.companyBaseName ||
  quotation.clientId?.customerName ||
  quotation.clientDetails?.companyBaseName ||
  quotation.clientDetails?.customerName ||
  quotation.client ||
  "";

const getBankCompanyKey = (quotation = {}) => {
  const rawCompany = String(
    quotation.company ||
      quotation.createdByCompany ||
      quotation.createdByUser ||
      "",
  ).toLowerCase();

  if (rawCompany.includes("optx")) return "Optx";
  if (rawCompany.includes("neon")) return "Neon";

  return "Optx";
};

const flowSteps = [
  "Approved",
  "Sent to customer",
  "View PDF",
  "Sign",
  "Submit",
];

const getIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const getQuotationClient = (quotation = {}) => {
  if (quotation.clientId && typeof quotation.clientId === "object") {
    return quotation.clientId;
  }

  return quotation.clientDetails || {};
};

const hasCompleteClientDetails = (client = {}) =>
  Boolean(
    client?.address &&
      client?.taxIdentificationNumber &&
      client?.contactPhoneNumber,
  );

const getSignatureDataUrl = (signaturePad) => {
  if (!signaturePad) return "";

  try {
    if (typeof signaturePad.getTrimmedCanvas === "function") {
      return signaturePad.getTrimmedCanvas().toDataURL("image/png");
    }
  } catch (error) {
    console.warn(
      "Unable to trim signature canvas, using full canvas instead.",
      error,
    );
  }

  if (typeof signaturePad.getCanvas === "function") {
    return signaturePad.getCanvas().toDataURL("image/png");
  }

  return "";
};

const hasSignaturePadValue = (signaturePad) =>
  Boolean(
    signaturePad &&
    typeof signaturePad.isEmpty === "function" &&
    !signaturePad.isEmpty(),
  );

const trimSignatureImage = (imageDataUrl) =>
  new Promise((resolve) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });

      if (!context) {
        resolve(imageDataUrl);
        return;
      }

      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      context.drawImage(image, 0, 0);

      const { data, width, height } = context.getImageData(
        0,
        0,
        canvas.width,
        canvas.height,
      );
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const index = (y * width + x) * 4;
          const red = data[index];
          const green = data[index + 1];
          const blue = data[index + 2];
          const alpha = data[index + 3];
          const isVisible = alpha > 24;
          const isWhiteBackground = red > 245 && green > 245 && blue > 245;

          if (isVisible && !isWhiteBackground) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (maxX < minX || maxY < minY) {
        resolve(imageDataUrl);
        return;
      }

      const signatureWidth = maxX - minX + 1;
      const signatureHeight = maxY - minY + 1;
      const padding = Math.max(
        12,
        Math.round(Math.max(signatureWidth, signatureHeight) * 0.08),
      );
      const cropX = Math.max(0, minX - padding);
      const cropY = Math.max(0, minY - padding);
      const cropWidth = Math.min(width - cropX, signatureWidth + padding * 2);
      const cropHeight = Math.min(height - cropY, signatureHeight + padding * 2);
      const outputCanvas = document.createElement("canvas");
      const outputContext = outputCanvas.getContext("2d");
      const maxOutputWidth = 520;
      const maxOutputHeight = 180;
      const outputScale = Math.min(
        1,
        maxOutputWidth / cropWidth,
        maxOutputHeight / cropHeight,
      );

      outputCanvas.width = Math.round(cropWidth * outputScale);
      outputCanvas.height = Math.round(cropHeight * outputScale);

      if (!outputContext) {
        resolve(imageDataUrl);
        return;
      }

      outputContext.fillStyle = "#ffffff";
      outputContext.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
      outputContext.drawImage(
        canvas,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputCanvas.width,
        outputCanvas.height,
      );

      const pngDataUrl = outputCanvas.toDataURL("image/png");
      if (pngDataUrl.length <= 60000) {
        resolve(pngDataUrl);
        return;
      }

      resolve(outputCanvas.toDataURL("image/jpeg", 0.7));
    };

    image.onerror = () => resolve(imageDataUrl);
    image.src = imageDataUrl;
  });

const CustomerSign = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const queryQuotationId =
    searchParams.get("quotationId") ||
    searchParams.get("quotationId".toLowerCase()) ||
    searchParams.get("id") ||
    "";
  const signatureRef = useRef(null);
  const signatureModalRef = useRef(null);
  const signatureUploadRef = useRef(null);
  const previewRef = useRef(null);
  const pageWrapperRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [linkInvalidMessage, setLinkInvalidMessage] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pdfPreviewData, setPdfPreviewData] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageRendered, setPageRendered] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(760);
  const [form, setForm] = useState({
    name: "",
    email: "",
    confirmed: false,
  });
  const [signed, setSigned] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureData, setSignatureData] = useState([]);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState("");
  const [signatureInputMode, setSignatureInputMode] = useState("draw");
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [acceptedSignatureUrl, setAcceptedSignatureUrl] = useState("");
  const [acceptedSignaturePdfUrl, setAcceptedSignaturePdfUrl] = useState("");
  const [signatureOverlayRect, setSignatureOverlayRect] = useState(null);

  const buildQuotationPdfBlob = useCallback(
    async ({ includeCustomerSignature = false } = {}) => {
      if (!quote) return null;

      const company = getBankCompanyKey(quote);
      const bankInfo = bankAccounts.companies?.[company] || {};
      const quotationData = {
        ...quote,
        clientDetails: quote.clientDetails || {},
        customerSignerName:
          quote.customerSignature?.signerName || getCustomerName(quote),
        customerSignatureImageUrl:
          includeCustomerSignature && acceptedSignaturePdfUrl
            ? acceptedSignaturePdfUrl
            : "",
      };

      return pdf(
        <QuotationPreview
          quotationData={quotationData}
          bankInfo={bankInfo}
          isDetailedForm={quote.isDetailedForm}
          isSpecialForm={quote.isSpecialForm}
          numberOfSpecialPages={quote.numberOfSpecialPages}
        />,
      ).toBlob();
    },
    [acceptedSignaturePdfUrl, quote],
  );

  const loadAcceptedSignatureUrl = useCallback(
    async (signatureUrl = "") => {
      if (!signatureUrl) {
        setAcceptedSignatureUrl("");
        setAcceptedSignaturePdfUrl("");
        return;
      }

      if (String(signatureUrl).startsWith("data:")) {
        setAcceptedSignatureUrl(signatureUrl);
        setAcceptedSignaturePdfUrl(signatureUrl);
        return;
      }

      try {
        const response = await axios.get(
          `${apiURL}customer-sign/${token}/signature-url`,
        );
        const signedImageUrl =
          response.data?.data?.signedImageUrl ||
          response.data?.signedImageUrl ||
          "";
        const imageDataUrl =
          response.data?.data?.imageDataUrl ||
          response.data?.imageDataUrl ||
          "";

        setAcceptedSignatureUrl(signedImageUrl || imageDataUrl);
        setAcceptedSignaturePdfUrl(imageDataUrl || signedImageUrl);
      } catch (error) {
        console.error("Error loading customer signature signed URL:", error);
        setAcceptedSignatureUrl("");
        setAcceptedSignaturePdfUrl("");
      }
    },
    [token],
  );

  useEffect(() => {
    const updateWidth = () => {
      if (!previewRef.current) return;
      setPreviewWidth(Math.min(previewRef.current.offsetWidth - 32, 760));
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    const fetchSigningData = async () => {
      if (!token) {
        setLinkInvalid(true);
        setLinkInvalidMessage("ลิงก์ลงนามนี้ไม่สามารถใช้งานได้แล้ว");
        setLoading(false);
        return;
      }

      try {
        const verifyResponse = await axios.get(
          `${apiURL}customer-sign/${token}`,
        );
        const verifiedData =
          verifyResponse.data?.data || verifyResponse.data || {};
        const verifiedQuotation =
          verifiedData.quotation || verifiedData.quotationData || null;
        const verifiedQuotationId =
          getIdValue(verifiedData.quotationId) ||
          getIdValue(verifiedData.quotation) ||
          getIdValue(verifiedData.quotationData);
        const resolvedQuotationId = verifiedQuotationId || queryQuotationId;

        if (!resolvedQuotationId && !verifiedQuotation) {
          throw new Error("Signing token did not return quotation data");
        }

        const quotation = verifiedQuotation
          ? verifiedQuotation
          : (await axios.get(`${apiURL}quotations/${resolvedQuotationId}`))
              .data || {};
        const quotationClient = getQuotationClient(quotation);
        let resolvedClientDetails = quotationClient;
        const clientId = getIdValue(quotation.clientId || quotation.clientDetails);

        if (clientId && !hasCompleteClientDetails(quotationClient)) {
          try {
            const clientResponse = await axios.get(`${apiURL}clients/${clientId}`);
            resolvedClientDetails =
              clientResponse.data?.data || clientResponse.data || quotationClient;
          } catch (clientError) {
            console.warn(
              "Unable to load full customer details for signing PDF.",
              clientError,
            );
          }
        }

        const quotationWithClient = {
          ...quotation,
          clientId:
            quotation.clientId && typeof quotation.clientId === "object"
              ? { ...quotation.clientId, ...resolvedClientDetails }
              : quotation.clientId,
          clientDetails: {
            ...quotationClient,
            ...resolvedClientDetails,
          },
        };
        const approval = quotation.customerApproval || {};
        const customerSignature = quotation.customerSignature || {};

        setQuote(quotationWithClient);
        await loadAcceptedSignatureUrl(customerSignature.imageUrl || "");
        setSigned(
          quotation.status === "Customer Accepted" ||
            quotation.status === "Accepted" ||
            approval.status === "Accepted",
        );
        setForm((current) => ({
          ...current,
          name:
            customerSignature.signerName ||
            getCustomerName(quotationWithClient),
          email: customerSignature.signerEmail || approval.to || "",
        }));
      } catch (error) {
        console.error("Error verifying customer signing link:", error);
        setLinkInvalid(true);
        setLinkInvalidMessage("ลิงก์ลงนามนี้ไม่สามารถใช้งานได้แล้ว");
      } finally {
        setLoading(false);
      }
    };

    fetchSigningData();
  }, [loadAcceptedSignatureUrl, queryQuotationId, token]);

  useEffect(() => {
    const generateQuotationPdf = async () => {
      if (!quote || pdfPreviewData) return;

      try {
        setGeneratingPdf(true);
        const blob = await buildQuotationPdfBlob();
        if (!blob) return;
        const arrayBuffer = await blob.arrayBuffer();
        setPdfPreviewData({ data: new Uint8Array(arrayBuffer) });
      } catch (error) {
        console.error("Error generating quotation PDF:", error);
        toast.error("ไม่สามารถสร้างตัวอย่าง PDF ได้");
      } finally {
        setGeneratingPdf(false);
      }
    };

    generateQuotationPdf();
  }, [buildQuotationPdfBlob, pdfPreviewData, quote]);

  const documentNo = useMemo(() => {
    if (!quote) return "Quotation";

    const year = quote.documentDate
      ? new Date(quote.documentDate).getFullYear()
      : new Date().getFullYear();
    const run = quote.runNumber?.toString().padStart(3, "0") || quote.no || "";
    const type = quote.type ? `(${quote.type})` : "";
    const prefix = COMPANY_PREFIX;

    return run
      ? `${prefix}${type}-${year}-${run}`
      : quote.documentNo || "Quotation";
  }, [quote]);

  const syncSignaturePad = useCallback(
    (signaturePad) => {
      if (!signaturePad) return;

      if (signatureData.length && typeof signaturePad.fromData === "function") {
        signaturePad.clear();
        signaturePad.fromData(signatureData);
      }
    },
    [signatureData],
  );

  const saveSignatureFromPad = useCallback((signaturePad) => {
    if (!hasSignaturePadValue(signaturePad)) {
      setHasSignature(false);
      setSignatureData([]);
      setSignaturePreviewUrl("");
      return;
    }

    if (typeof signaturePad.toData === "function") {
      setSignatureData(signaturePad.toData());
    }
    setSignaturePreviewUrl(getSignatureDataUrl(signaturePad));
    setSignatureInputMode("draw");
    setHasSignature(true);
  }, []);

  const handleSignatureUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const imageDataUrl = String(reader.result || "");

      if (!imageDataUrl) {
        toast.error("Unable to read signature image");
        return;
      }

      const trimmedImageDataUrl = await trimSignatureImage(imageDataUrl);

      signatureRef.current?.clear();
      signatureModalRef.current?.clear();
      setSignatureData([]);
      setSignaturePreviewUrl(trimmedImageDataUrl);
      setSignatureInputMode("upload");
      setHasSignature(true);
    };
    reader.onerror = () => {
      toast.error("Unable to read signature image");
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!isSignatureModalOpen) return undefined;

    const timer = window.setTimeout(() => {
      syncSignaturePad(signatureModalRef.current);
    }, 60);

    return () => window.clearTimeout(timer);
  }, [isSignatureModalOpen, syncSignaturePad]);

  const openSignatureModal = () => {
    if (signed) return;
    setIsSignatureModalOpen(true);
  };

  const closeSignatureModal = () => {
    if (hasSignaturePadValue(signatureModalRef.current)) {
      saveSignatureFromPad(signatureModalRef.current);
    }
    setIsSignatureModalOpen(false);
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    signatureModalRef.current?.clear();
    setSignatureData([]);
    setSignaturePreviewUrl("");
    setSignatureInputMode("draw");
    setHasSignature(false);
  };

  const previewFile = pdfPreviewData;
  const isLastPage = Boolean(numPages && pageNumber === numPages);
  const overlayScale = previewWidth / 760;
  const fallbackSignatureOverlayStyle = {
    left: `${50 * overlayScale}px`,
    bottom: `${340 * overlayScale}px`,
    width: `${178 * overlayScale}px`,
    height: `${60 * overlayScale}px`,
  };
  const signatureOverlayStyle =
    signatureOverlayRect || fallbackSignatureOverlayStyle;

  const updateSignatureOverlayPosition = useCallback(() => {
    if (!pageWrapperRef.current || !isLastPage) {
      setSignatureOverlayRect(null);
      return;
    }

    const pageRect = pageWrapperRef.current.getBoundingClientRect();
    const textSpans = Array.from(
      pageWrapperRef.current.querySelectorAll(
        ".react-pdf__Page__textContent span",
      ),
    );
    const customerLabel = textSpans.find(
      (span) => span.textContent?.trim() === "Customer",
    );

    if (!customerLabel) {
      setSignatureOverlayRect(null);
      return;
    }

    const labelRect = customerLabel.getBoundingClientRect();
    const boxWidth = 178 * overlayScale;
    const boxHeight = 60 * overlayScale;
    const lineGap = 14 * overlayScale;
    const labelCenterX = labelRect.left - pageRect.left + labelRect.width / 2;
    const top = labelRect.top - pageRect.top - boxHeight - lineGap;

    setSignatureOverlayRect({
      left: `${labelCenterX - boxWidth / 2}px`,
      top: `${top}px`,
      width: `${boxWidth}px`,
      height: `${boxHeight}px`,
    });
  }, [isLastPage, overlayScale]);

  useEffect(() => {
    const timer = window.setTimeout(updateSignatureOverlayPosition, 80);
    return () => window.clearTimeout(timer);
  }, [pageNumber, previewFile, previewWidth, updateSignatureOverlayPosition]);

  useEffect(() => {
    setPageRendered(false);
  }, [pageNumber, previewFile, previewWidth]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim()) {
      toast.error("กรุณากรอกชื่อและอีเมล");
      return;
    }

    if (!form.confirmed) {
      toast.error("กรุณายืนยันก่อนส่งเอกสาร");
      return;
    }

    const latestSignaturePad = hasSignaturePadValue(signatureModalRef.current)
      ? signatureModalRef.current
      : signatureRef.current;

    if (!signaturePreviewUrl && !hasSignaturePadValue(latestSignaturePad)) {
      toast.error("กรุณาลงลายเซ็น");
      return;
    }

    try {
      setSubmitting(true);
      const signatureImageBase64 =
        signaturePreviewUrl || getSignatureDataUrl(latestSignaturePad);

      if (!signatureImageBase64) {
        toast.error("ไม่สามารถอ่านรูปลายเซ็นได้");
        return;
      }

      const response = await axios.post(
        `${apiURL}customer-sign/${token}/accept`,
        {
          quotationId: quote?._id || quote?.id,
          token,
          signerName: form.name.trim(),
          signerEmail: form.email.trim(),
          signatureImageBase64,
          consentAccepted: true,
        },
      );
      const savedSignatureUrl =
        response.data?.data?.customerSignature?.signedImageUrl ||
        signatureImageBase64;
      const savedSignaturePdfUrl =
        response.data?.data?.customerSignature?.imageDataUrl ||
        signatureImageBase64;

      setAcceptedSignatureUrl(savedSignatureUrl);
      setAcceptedSignaturePdfUrl(savedSignaturePdfUrl);
      setSigned(true);
      toast.success("ส่งลายเซ็นเรียบร้อยแล้ว");
    } catch (error) {
      console.error("Error submitting customer signature:", error);
      toast.error("ส่งลายเซ็นไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!quote) return;

    try {
      setDownloadingPdf(true);
      const blob = await buildQuotationPdfBlob({
        includeCustomerSignature: signed,
      });

      if (!blob) {
        toast.error("ไม่สามารถสร้างไฟล์ PDF ได้");
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeDocumentNo = String(documentNo || "quotation").replace(
        /[^a-zA-Z0-9._()-]+/g,
        "-",
      );

      link.href = objectUrl;
      link.download = `${safeDocumentNo}${signed ? "-accepted" : ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading quotation PDF:", error);
      toast.error("ดาวน์โหลด PDF ไม่สำเร็จ");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <ClipLoader color="#6366F1" size={58} />
      </div>
    );
  }

  if (linkInvalid) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
          <div className="w-full rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300">
              <HiOutlineDocumentText className="h-6 w-6" />
            </div>
            <h1 className="mt-5 text-xl font-bold">
              {linkInvalidMessage || "ลิงก์ลงนามนี้ไม่สามารถใช้งานได้แล้ว"}
            </h1>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              เอกสารนี้อาจถูกยกเลิก รีเซ็ต หรือมีการสร้างลิงก์ใหม่แล้ว
              กรุณาติดต่อเจ้าหน้าที่เพื่อขอลิงก์ลงนามใหม่
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-400">
                Customer E-Sign
              </p>
              <h1 className="mt-1 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {documentNo}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {quote?.projectName || quote?.subject || "Quotation document"}
              </p>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
              <HiOutlineCheckCircle className="h-5 w-5" />
              {signed ? "Customer Accepted" : "Ready for signature"}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="min-w-0">
          <div className="mb-5 grid gap-2 sm:grid-cols-5">
            {flowSteps.map((step, index) => (
              <div
                key={step}
                className={`rounded-md border px-3 py-2 text-xs font-semibold ${
                  index <= (signed ? 4 : 3)
                    ? "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/60 dark:bg-indigo-950/40 dark:text-indigo-300"
                    : "border-gray-200 bg-white text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400"
                }`}
              >
                <span className="mr-1.5">{index + 1}.</span>
                {step}
              </div>
            ))}
          </div>

          <div
            ref={previewRef}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300">
                  <HiOutlineDocumentText className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-base font-bold">PDF Preview</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Page {pageNumber} {numPages ? `of ${numPages}` : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {signed && (
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={!quote || generatingPdf || downloadingPdf}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-indigo-300 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                  >
                    {downloadingPdf ? (
                      <ClipLoader color="#6366F1" size={14} />
                    ) : (
                      <HiOutlineDownload className="h-4 w-4" />
                    )}
                    Download PDF
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPageNumber((page) => Math.max(1, page - 1))}
                  disabled={pageNumber <= 1}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-indigo-300 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setPageNumber((page) =>
                      numPages ? Math.min(numPages, page + 1) : page,
                    )
                  }
                  disabled={!numPages || pageNumber >= numPages}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-indigo-300 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex min-h-[520px] justify-center overflow-auto rounded-md border border-gray-100 bg-gray-100 p-3 dark:border-gray-800 dark:bg-gray-950">
              {previewFile ? (
                <Document
                  file={previewFile}
                  loading={<ClipLoader color="#6366F1" size={36} />}
                  onLoadSuccess={({ numPages: loadedPages }) => {
                    setNumPages(loadedPages);
                    setPageNumber(loadedPages);
                    setPageRendered(false);
                  }}
                  onLoadError={(error) => {
                    console.error("PDF preview error:", error);
                    toast.error("ไม่สามารถแสดง PDF ได้");
                  }}
                >
                  <div
                    ref={pageWrapperRef}
                    className="relative bg-white shadow-sm [&_.react-pdf__Page__textContent]:opacity-0 [&_.react-pdf__Page__textContent]:pointer-events-none"
                  >
                    <Page
                      key={`${pageNumber}-${previewWidth}`}
                      pageNumber={pageNumber}
                      width={previewWidth}
                      loading={
                        <div
                          className="flex items-center justify-center bg-white"
                          style={{
                            width: `${previewWidth}px`,
                            height: `${previewWidth * 1.414}px`,
                          }}
                        >
                          <ClipLoader color="#6366F1" size={36} />
                        </div>
                      }
                      renderAnnotationLayer={false}
                      renderTextLayer
                      onRenderSuccess={() => {
                        setPageRendered(true);
                        updateSignatureOverlayPosition();
                      }}
                      onRenderError={(error) => {
                        console.error("PDF page render error:", error);
                      }}
                    />

                    {pageRendered && isLastPage && !signed && (
                      <button
                        type="button"
                        onClick={openSignatureModal}
                        className="absolute z-10 overflow-hidden rounded-sm border-2 border-blue-500 bg-white/90 shadow-[0_0_0_1px_rgba(255,255,255,0.75)] transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                        style={signatureOverlayStyle}
                        aria-label="Open customer signature box"
                      >
                        {signaturePreviewUrl ? (
                          <img
                            src={signaturePreviewUrl}
                            alt="Customer signature preview"
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center gap-1 px-1 text-[10px] font-bold text-blue-700 sm:text-xs">
                            <HiOutlinePencilAlt className="h-3.5 w-3.5" />
                            Tap to sign
                          </span>
                        )}
                      </button>
                    )}

                    {pageRendered && isLastPage && signed && acceptedSignatureUrl && (
                      <div
                        className="absolute z-10 pointer-events-none"
                        style={signatureOverlayStyle}
                      >
                        <img
                          src={acceptedSignatureUrl}
                          alt="Customer signature"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </Document>
              ) : generatingPdf ? (
                <div className="flex min-h-[440px] w-full items-center justify-center">
                  <ClipLoader color="#6366F1" size={42} />
                </div>
              ) : (
                <div className="flex min-h-[440px] w-full items-center justify-center rounded-md border border-dashed border-gray-300 bg-white text-center dark:border-gray-700 dark:bg-gray-900">
                  <div>
                    <HiOutlineDocumentText className="mx-auto h-10 w-10 text-gray-400" />
                    <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
                      ไม่สามารถแสดงเอกสารได้
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      ลิงก์ลงนามไม่สามารถโหลดเอกสารนี้ได้
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="lg:sticky lg:top-5 lg:self-start">
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="mb-5">
              <h2 className="text-base font-bold">Sign & Submit</h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Fill your details, sign in the Customer box on the PDF, then
                submit.
              </p>
            </div>

            <label className="mb-4 block">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
                <HiOutlineUser className="h-4 w-4 text-gray-400" />
                Name
              </span>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={signed}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-950 dark:focus:ring-indigo-950 dark:disabled:bg-gray-800"
                placeholder="Customer name"
              />
            </label>

            <label className="mb-4 block">
              <span className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
                <HiOutlineMail className="h-4 w-4 text-gray-400" />
                Email
              </span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                disabled={signed}
                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100 dark:border-gray-700 dark:bg-gray-950 dark:focus:ring-indigo-950 dark:disabled:bg-gray-800"
                placeholder="customer@example.com"
              />
            </label>

            <SignatureStatusPanel
              hasSignature={hasSignature}
              signed={signed}
              onOpenSignature={openSignatureModal}
              onClearSignature={clearSignature}
            />

            <label className="mb-5 flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-200">
              <input
                type="checkbox"
                name="confirmed"
                checked={form.confirmed}
                onChange={handleChange}
                disabled={signed}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                I confirm that I have reviewed this quotation and authorize this
                electronic signature.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || signed}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? (
                <ClipLoader color="#ffffff" size={18} />
              ) : (
                <HiOutlineCheckCircle className="h-5 w-5" />
              )}
              {signed ? "Customer Accepted" : "Sign & Submit"}
            </button>
          </form>
        </aside>
      </main>

      <SignatureModal
        isOpen={isSignatureModalOpen}
        signed={signed}
        signatureModalRef={signatureModalRef}
        signatureUploadRef={signatureUploadRef}
        signatureInputMode={signatureInputMode}
        signaturePreviewUrl={signaturePreviewUrl}
        onClose={closeSignatureModal}
        onClear={clearSignature}
        onUpload={handleSignatureUpload}
        onSaveFromPad={saveSignatureFromPad}
      />
    </div>
  );
};

export default CustomerSign;
